import { message, type UploadFile } from 'antd'
import { postJSON } from '../services/api'

export interface UploadedDocument {
  id: string
  name: string
  pdf: {
    bucket: string
    filename: string
    path: string
    url: string
  }
}

interface PresignResponse {
  data: {
    method: string
    url: string
    key: string
    expiresInSeconds: number
  }
}

interface CommitResponse {
  data: UploadedDocument
}

const FALLBACK_CONTENT_TYPE = 'application/octet-stream'

// Every extension the attachment inputs accept, so a blank `File.type` still
// yields a truthful type. Windows resolves MIME types from the registry, and
// notably has no HEIC mapping without the HEIF Image Extension installed.
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  csv: 'text/csv',
  doc: 'application/msword',
  docm: 'application/vnd.ms-word.document.macroEnabled.12',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gif: 'image/gif',
  heic: 'image/heic',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

/**
 * The presign endpoint rejects a falsy `contentType` with a 400, and the browser
 * reports `File.type` as '' whenever it can't infer one from the extension — so
 * a blank type has to be resolved here or the upload never starts.
 */
const resolveContentType = (file: File): string => {
  if (file.type) {
    return file.type
  }

  const extension = file.name.split('.').pop()?.toLowerCase()

  return (
    (extension && EXTENSION_CONTENT_TYPES[extension]) || FALLBACK_CONTENT_TYPE
  )
}

/**
 * Uploads a file using the presign/commit flow
 * @param file - The file to upload
 * @param residentId - The resident ID
 * @param categoryId - The category ID (Communication Records or Referrals)
 * @returns The uploaded document object
 */
export const uploadDocument = async (
  file: File,
  residentId: string,
  categoryId: string
): Promise<UploadedDocument> => {
  const contentType = resolveContentType(file)

  try {
    // Step 1: Get presigned URL
    const presignResponse = (await postJSON({
      url: '/api/list/documents/uploads/presign',
      method: 'POST',
      json: {
        filename: file.name,
        contentType,
        sizeBytes: file.size,
        residentId,
        categoryId
      }
    })) as PresignResponse

    const { url, key, method } = presignResponse.data

    // Step 2: Upload directly to S3 using presigned URL
    const uploadResponse = await fetch(url, {
      method: method || 'PUT',
      // S3 signs ContentType into the presigned URL, and the browser sends no
      // Content-Type at all for a blank-type File — so send the presigned value
      // explicitly or the PUT fails with SignatureDoesNotMatch.
      headers: { 'Content-Type': contentType },
      body: file
    })

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text()
      // Surface only S3's <Code> (e.g. SignatureDoesNotMatch, AccessDenied,
      // RequestTimeTooSkewed) — never log the raw XML body, which can contain
      // access keys or object paths (including a resident's file name), and
      // which would otherwise leak into Sentry via console breadcrumbs.
      const s3Code = errorText.match(/<Code>([^<]+)<\/Code>/)?.[1]
      const detail = `S3 upload failed: ${uploadResponse.status} ${
        uploadResponse.statusText
      }${s3Code ? ` (${s3Code})` : ''}`
      console.error(detail)
      throw new Error(detail)
    }

    // Step 3: Commit the upload
    const commitResponse = (await postJSON({
      url: '/api/list/documents/uploads/commit',
      method: 'POST',
      json: {
        key,
        resident: residentId,
        name: file.name,
        category: categoryId
      }
    })) as CommitResponse

    return commitResponse.data
  } catch (error) {
    console.error('Document upload failed:', error)
    message.error('Failed to upload document. Please try again.')
    throw error
  }
}

/**
 * Extracts a human-readable reason from an upload failure. `postJSON` throws
 * the raw response object (carrying `message`/`msg`) rather than an Error when
 * the API returns those fields, so `String(error)` alone would collapse the
 * presign/commit detail into "[object Object]".
 */
const describeError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  if (error && typeof error === 'object') {
    const { message, msg } = error as { message?: unknown; msg?: unknown }
    if (typeof message === 'string') {
      return message
    }
    if (typeof msg === 'string') {
      return msg
    }
  }
  return String(error)
}

/**
 * Uploads multiple files using the presign/commit flow
 * @param files - Array of Ant Design UploadFile objects (max 3)
 * @param residentId - The resident ID
 * @param categoryId - The category ID
 * @returns Array of document IDs
 */
export const uploadDocuments = async (
  files: UploadFile[],
  residentId: string,
  categoryId: string
): Promise<string[]> => {
  const documentIds: string[] = []
  const failures: string[] = []

  for (const uploadFile of files) {
    if (uploadFile.originFileObj) {
      try {
        const doc = await uploadDocument(
          uploadFile.originFileObj,
          residentId,
          categoryId
        )
        documentIds.push(doc.id)
      } catch (error) {
        // If one upload fails, record why and continue with the others
        const reason = describeError(error)
        console.error(`Failed to upload ${uploadFile.name}: ${reason}`)
        failures.push(reason)
      }
    }
  }

  if (documentIds.length === 0 && files.length > 0) {
    // Surface the underlying reason(s) so Sentry shows *why* every upload
    // failed. An empty list means the files arrived without an originFileObj.
    const detail =
      failures.length > 0
        ? failures.join('; ')
        : 'no uploadable files (missing originFileObj)'
    throw new Error(`All document uploads failed: ${detail}`)
  }

  return documentIds
}
